import React, { useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Pin, PinOff, Check, Loader2 } from 'lucide-react';
import { usePinnedCharts } from '../hooks/usePinnedCharts';

const Button = styled(motion.button)`
  background: ${props => props.$isPinned
    ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
    : 'linear-gradient(135deg, #1A8B8B 0%, #0D6B6B 100%)'
  };
  color: white;
  border: none;
  border-radius: 6px;
  padding: 8px 16px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: ${props => props.$isPinned
      ? '0 4px 12px rgba(16, 185, 129, 0.3)'
      : '0 4px 12px rgba(26, 139, 139, 0.4)'
    };
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }

  svg {
    width: 14px;
    height: 14px;
  }

  .spinner {
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

const PinButton = ({
  chartSpec,
  sqlQuery,
  originalQuestion,
  title,
  columnNames,
  cachedResults,
  dbName = 'kfc_postgres',
  onPinSuccess,
  onUnpinSuccess
}) => {
  const { pinChart, unpinChart, isChartPinnedBySql, pinningInProgress, pinnedCharts } = usePinnedCharts();
  const [localLoading, setLocalLoading] = useState(false);

  // Use SQL query as unique identifier (since frontend message IDs aren't DB UUIDs)
  const isPinned = isChartPinnedBySql(sqlQuery);
  const pinnedChart = pinnedCharts.find(c => c.sql_query === sqlQuery);
  const isLoading = localLoading || pinningInProgress;

  const handleClick = async () => {
    setLocalLoading(true);

    try {
      if (isPinned && pinnedChart) {
        // Unpin
        const result = await unpinChart(pinnedChart.id);
        if (result.success && onUnpinSuccess) {
          onUnpinSuccess();
        }
      } else {
        // Pin - don't send message_id/session_id as they're not DB UUIDs
        const chartData = {
          title: title || originalQuestion?.substring(0, 100) || 'Pinned Chart',
          original_question: originalQuestion || '',
          chart_spec: chartSpec,
          sql_query: sqlQuery,
          cached_results: cachedResults,
          column_names: columnNames,
          db_name: dbName
        };

        const result = await pinChart(chartData);
        if (result.success && onPinSuccess) {
          onPinSuccess(result.chart);
        }
      }
    } finally {
      setLocalLoading(false);
    }
  };

  // Don't render if no chart spec or SQL query (required for pin)
  if (!chartSpec || !sqlQuery) {
    return null;
  }

  return (
    <Button
      onClick={handleClick}
      disabled={isLoading}
      $isPinned={isPinned}
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
    >
      {isLoading ? (
        <>
          <Loader2 className="spinner" />
          {isPinned ? 'جاري الإزالة...' : 'جاري التثبيت...'}
        </>
      ) : isPinned ? (
        <>
          <Check />
          مثبت في لوحة المعلومات
        </>
      ) : (
        <>
          <Pin />
          تثبيت في لوحة المعلومات
        </>
      )}
    </Button>
  );
};

export default PinButton;
